/**
 * Cost-category taxonomy for ROI value streams. A `cost`-direction value stream
 * carries one of these categories; the model-generation gate rejects categories
 * that are logically inapplicable to the selected implementation approach
 * (e.g. a vendor subscription on an internal build). See ELI-864.
 */

// The taxonomy is declared as three sub-groups (vendor / internal / shared);
// the full category list and the `CostCategory` type are composed from them,
// so there is a single source of truth. Zod schemas import
// `COST_CATEGORY_VALUES` instead of re-listing the members inline.

/** Costs that only arise when paying an external vendor. */
export const VENDOR_COST_CATEGORIES = [
  "vendor_subscription",
  "vendor_implementation",
] as const;

/** Costs incurred by internal teams — building, maintaining, operating. */
export const INTERNAL_COST_CATEGORIES = [
  "internal_build_labor",
  "internal_maintenance",
  "internal_operating_labor",
] as const;

/** Costs that apply regardless of build-vs-buy. */
export const SHARED_COST_CATEGORIES = [
  "integration",
  "infrastructure",
  "other",
] as const;

/** Every cost category, composed from the sub-groups above. */
export const COST_CATEGORY_VALUES = [
  ...VENDOR_COST_CATEGORIES,
  ...INTERNAL_COST_CATEGORIES,
  ...SHARED_COST_CATEGORIES,
] as const;

export type CostCategory = (typeof COST_CATEGORY_VALUES)[number];

/** The four decided implementation approaches. Mirrors
 *  `BuildBuyDirection["direction"]` minus `undecided`, and `CostStructure`
 *  (model-generation) minus `unknown`. Defined here (the lowest-level package)
 *  so the gate, the flag detector, and the web app can all import it without a
 *  dependency cycle. */
export type ImplementationApproach =
  | "build"
  | "buy"
  | "hybrid"
  | "extend_existing";

export const APPROACH_ALLOWED_COST_CATEGORIES: Record<
  ImplementationApproach,
  ReadonlySet<CostCategory>
> = {
  // Internal work plus the shared categories — no vendor line items.
  build: new Set<CostCategory>([
    ...INTERNAL_COST_CATEGORIES,
    ...SHARED_COST_CATEGORIES,
  ]),
  // Vendor line items, integration, ongoing operating labor, and the `other`
  // fallback — but no in-house build/maintenance labor or net-new infrastructure.
  buy: new Set<CostCategory>([
    ...VENDOR_COST_CATEGORIES,
    "integration",
    "internal_operating_labor",
    "other",
  ]),
  // Same envelope as a greenfield build: internal work plus shared categories.
  extend_existing: new Set<CostCategory>([
    ...INTERNAL_COST_CATEGORIES,
    ...SHARED_COST_CATEGORIES,
  ]),
  hybrid: new Set<CostCategory>(COST_CATEGORY_VALUES),
};

export function isCostCategoryAllowed(
  approach: ImplementationApproach,
  category: CostCategory,
): boolean {
  return APPROACH_ALLOWED_COST_CATEGORIES[approach].has(category);
}
